import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping';
import {
  useParams,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { useSublocations } from '../hooks/useSublocations';
import {
  Box,
  VStack,
  Button,
  Text,
  Spinner,
  useToast,
  Container,
  useColorModeValue
} from '@chakra-ui/react';

import { getSiteProfile, getSiteDiscussions } from '../firebase/siteProfile';
import { useAuth } from '../hooks/useAuth';
import { useMedia } from '../hooks/useMedia';
import SiteInfo from '../components/SiteInfo';
import MediaGallery from '../components/media/MediaGallery';
import CommentSection from '../components/comment-section/CommentSection';
import ChatBot from '../components/site/ChatBot';
import MobileMediaFilters from '../components/media/MobileMediaFilters';
import { getMediaFeatures, generateFeatureVariations } from '../utils/featureUtils';

export default function SiteProfile() {
  // Router hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Context hooks
  const { user } = useAuth();
  const toast = useToast();

  // Color mode hooks
  // Use semantic tokens directly where needed, e.g., bg="bg.canvas", color="text.default"
  // const bgColor = useColorModeValue('ancient.background', 'gray.900'); // Replaced with bg.canvas
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900'); // Keep for specific overrides if needed, prefer text.default/muted
  // const borderColor = useColorModeValue('gray.200', 'gray.700'); // Replaced with border.default

  // State hooks
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]); 
  const [filters, setFilters] = useState({ features: [], featureFilterLogic: 'AND' });
  const [hiddenLocations, setHiddenLocations] = useState({});

  // Parse URL parameters for search context
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const features = searchParams.get('features');
    const featureLogic = searchParams.get('featureLogic');
    
    return {
      features: features ? features.split(',').filter(f => f.trim()) : [],
      featureFilterLogic: featureLogic || 'AND'
    };
  }, [location.search]);

  // Apply URL filters on component mount
  useEffect(() => {
    if (urlParams.features.length > 0) {
      setFilters(urlParams);
    }
  }, [urlParams]);

  // Media hooks
  const { media, loading: mediaLoading, error: mediaError, loadMedia, uploadMedia } = useMedia(site?.id);
  const { sublocations } = useSublocations(site?.id);

  // Augment media with sublocation data
  const augmentedMedia = useMemo(() => {
    if (!media || !sublocations) return media;
    
    return media.map(item => {
      if (!item.subLocationId) return item;
      
      const sublocation = sublocations.find(sub => sub.id === item.subLocationId);
      if (!sublocation) return item;
      
      return {
        ...item,
        subLocation: {
          id: sublocation.id,
          name: sublocation.name,
          coordinates: sublocation.coordinates
        }
      };
    });
  }, [media, sublocations]);

  // Filter media by selected features using enhanced feature normalization
  const filteredMedia = useMemo(() => {
    if (!filters.features?.length) return null;
    if (!augmentedMedia) return [];

    console.log('🔍 Filtering media with filters:', filters);
    console.log('📁 Total augmented media:', augmentedMedia.length);

    const uniqueMediaIds = new Set();
    const filtered = [];

    // Create a map of normalized features to search for
    const searchFeatureVariations = new Map();
    filters.features.forEach(feature => {
      const variations = generateFeatureVariations(feature);
      console.log(`🔄 Generated variations for "${feature}":`, variations);
      variations.forEach(variation => {
        searchFeatureVariations.set(variation.toLowerCase(), feature);
      });
    });

    console.log('🗺️ Search feature variations map:', searchFeatureVariations);

    augmentedMedia.forEach((mediaItem, index) => {
      if (uniqueMediaIds.has(mediaItem.id)) return;

      // Get all features for this media item using our utility
      const mediaFeatures = getMediaFeatures(mediaItem);
      const mediaFeatureIds = new Set(mediaFeatures.map(f => f.id.toLowerCase()));

      console.log(`📷 Media item ${index} (${mediaItem.id}):`, {
        features: mediaFeatures,
        featureIds: Array.from(mediaFeatureIds)
      });

      // Check if media matches any of the search feature variations
      const matchingFeatures = new Set();
      for (const mediaFeatureId of mediaFeatureIds) {
        if (searchFeatureVariations.has(mediaFeatureId)) {
          matchingFeatures.add(searchFeatureVariations.get(mediaFeatureId));
          console.log(`✅ Match found: "${mediaFeatureId}" matches filter "${searchFeatureVariations.get(mediaFeatureId)}"`);
        }
      }

      // Apply AND/OR logic
      let matchesFilter = false;
      if (filters.featureFilterLogic === 'OR') {
        // OR: media must have at least one of the selected features
        matchesFilter = matchingFeatures.size > 0;
      } else {
        // AND: media must have all of the selected features
        matchesFilter = filters.features.every(feature => matchingFeatures.has(feature));
      }

      console.log(`🎯 Media item ${index} matches filter:`, matchesFilter, 'matching features:', Array.from(matchingFeatures));

      if (matchesFilter) {
        uniqueMediaIds.add(mediaItem.id);
        filtered.push({
          ...mediaItem,
          siteId: site.id
        });
      }
    });

    console.log('✅ Final filtered media count:', filtered.length);
    return filtered;
  }, [augmentedMedia, filters.features, filters.featureFilterLogic, site]);

  // Handle feature click in features section
  const handleFeatureClick = useCallback((feature) => {
    setFilters(prev => {
      const newFeatures = prev.features?.includes(feature.id)
        ? prev.features.filter(f => f !== feature.id)
        : [...(prev.features || []), feature.id];
      return { ...prev, features: newFeatures };
    });
  }, []);

  // Process grounding articles
  const groundingArticles = useMemo(() => {
    if (!site?.groundingMetadata?.groundingChunks) return [];

    return site.groundingMetadata.groundingChunks
      .filter(chunk => chunk.web && chunk.web.uri && chunk.web.title)
      .map(chunk => ({
        type: 'article',
        url: chunk.web.uri,
        title: chunk.web.title,
        description: chunk.web.snippet || '',
      }));
  }, [site?.groundingMetadata]);

  const enrichedSite = useMemo(() => {
    if (!site) return null;
    return {
      ...site,
      groundingArticles
    };
  }, [site, groundingArticles]);

  // Fetch site + discussions
  const fetchSiteData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching site data for ID:', id);
      const siteRes = await getSiteProfile(id);
      if (!siteRes.success) {
        console.error('Error fetching site:', siteRes.error);
        toast({
          title: 'Error',
          description: siteRes.error,
          status: 'error',
          isClosable: true
        });
        setLoading(false);
        return;
      }
      console.log('Site data fetched:', siteRes.data);
      
      // Ensure construction periods are properly handled
      const siteData = siteRes.data;
      if (!siteData.constructionPeriods) {
        siteData.constructionPeriods = [];
      }
      
      setSite(siteData);

      const discussionsRes = await getSiteDiscussions(id);
      if (discussionsRes.success) {
        setComments(discussionsRes.data);
      }
    } catch (err) {
      console.error('Error in fetchSiteData:', err);
      toast({
        title: 'Error',
        description: 'Failed to load site data',
        status: 'error',
        isClosable: true
      });
    }
    setLoading(false);
  }, [id, toast]);

  const handleMediaUploadComplete = useCallback(async (mediaData) => {
    console.log('Media upload data:', mediaData);
    try {
      if (mediaData) {
        const result = await uploadMedia(mediaData);
        if (!result || !result.success) {
          throw new Error(result?.error || 'Unknown upload error');
        }
      }
      await loadMedia();
      
      toast({
        title: 'Media uploaded',
        description: 'Site media updated successfully',
        status: 'success',
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading media:', error);
      toast({
        title: 'Upload Error',
        description: 'Failed to update site media: ' + error.message,
        status: 'error',
        isClosable: true
      });
    }
  }, [uploadMedia, loadMedia, toast]);

  const handleSiteUpdate = useCallback(async (updatedSite) => {
    setSite(updatedSite);
    toast({
      title: 'Success',
      description: 'Site updated successfully',
      status: 'success',
      duration: 5000,
      isClosable: true
    });
  }, [toast]);

  // Effects
  useEffect(() => {
    fetchSiteData();
  }, [fetchSiteData]);

  // Clean up any stale CreateSite AI Assistant elements
  useEffect(() => {
    const cleanupStaleElements = () => {
      // More aggressive cleanup for mobile
      const selectors = [
        '[data-component="create-site-ai-assistant"]',
        '[key*="ai-assistant"]',
        'button[aria-label*="AI Assistant"]:not([data-component="site-profile-chatbot"])',
        '[style*="LightbulbFilament"]',
        // Target mobile-specific positioning
        '[style*="position: fixed"][style*="bottom: 36px"]',
        '[style*="position: fixed"][style*="bottom: 144px"]',
        // Target Chakra UI buttons with yellow/gold styling
        '.chakra-button[style*="position: fixed"][style*="bottom"]:not([data-component="site-profile-chatbot"])',
        // Target any fixed positioned elements in bottom right that aren't our chatbot
        '[style*="position: fixed"][style*="bottom"][style*="right"]:not([data-component="site-profile-chatbot"])',
        // Target any yellow/gold floating buttons
        '[style*="background"][style*="yellow"]:not([data-component="site-profile-chatbot"])',
        '[style*="background-color"][style*="yellow"]:not([data-component="site-profile-chatbot"])',
        '[style*="background"][style*="gold"]:not([data-component="site-profile-chatbot"])',
        '[style*="background-color"][style*="gold"]:not([data-component="site-profile-chatbot"])',
        // Target buttons with common AI assistant icon classes
        '[class*="assistant-button"]:not([data-component="site-profile-chatbot"])',
        '[class*="ai-button"]:not([data-component="site-profile-chatbot"])',
        '[class*="chat-button"]:not([data-component="site-profile-chatbot"])',
        // Target any gradient buttons that might be yellow/gold
        '[style*="linear-gradient"]:not([data-component="site-profile-chatbot"])'
      ];

      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            // Double-check it's not our SiteProfile chatbot
            if (element && 
                element.getAttribute('data-component') !== 'site-profile-chatbot' &&
                !element.closest('[data-component="site-profile-chatbot"]')) {
              console.log('Removing stale AI assistant element:', element);
              if (element.parentNode) {
                element.parentNode.removeChild(element);
              }
            }
          });
        } catch (e) {
          console.warn('Error cleaning up element with selector:', selector, e);
        }
      });
    };

    // Clean up immediately, after a short delay, and periodically
    cleanupStaleElements();
    const timeoutId = setTimeout(cleanupStaleElements, 100);
    const intervalId = setInterval(cleanupStaleElements, 1000); // Check every second for 10 seconds
    const clearIntervalId = setTimeout(() => clearInterval(intervalId), 10000);

    // Set up MutationObserver to catch dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node or its children contain CreateSite AI Assistant elements
            const element = node;
            if (element.getAttribute && 
                (element.getAttribute('data-component') === 'create-site-ai-assistant' ||
                 element.querySelector && element.querySelector('[data-component="create-site-ai-assistant"]'))) {
              console.log('Detected dynamically added CreateSite AI Assistant, removing...');
              setTimeout(cleanupStaleElements, 10);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      clearTimeout(clearIntervalId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (site?.id) {
      console.log('Loading media for site:', site.id);
      loadMedia();
    }
  }, [site?.id, loadMedia]);

  // Error logging
  useEffect(() => {
    if (mediaError) {
      console.error('Media loading error:', mediaError);
    }
  }, [mediaError]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={{ base: 6, md: 10 }} bg="bg.canvas"> {/* Responsive padding + Theme BG */}
        <VStack spacing={4} align="center">
          <Text color="text.default">Loading site profile...</Text> {/* Theme text color */}
          <Spinner color="brand.primary.500" /> {/* Theme spinner color */}
        </VStack>
      </Container>
    );
  }

  if (!site) {
    return (
      <Container maxW="container.xl" py={{ base: 6, md: 10 }} bg="bg.canvas" color="text.default"> {/* Responsive padding + Theme BG/Text */}
        <VStack spacing={4}>
          <Text color="text.error">Error loading site profile</Text> {/* Theme error color */}
          <Button
            onClick={() => navigate('/search')}
            colorScheme="brand.primary" // Use theme color scheme
            variant="outline"
          >
            Back to Search
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Box bg="bg.default" minH="100vh">
      {/* Hero Section */}
      <Box
        position="relative"
        bg="linear-gradient(135deg, bg.surface 0%, bg.accent 100%)"
        borderBottom="1px solid"
        borderColor="border.light"
        overflow="hidden"
      >
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.05}
          backgroundImage="radial-gradient(circle at 25% 25%, currentColor 2px, transparent 2px)"
          backgroundSize="24px 24px"
          color="text.default"
        />
        
        <Container maxW="container.xl" py={{ base: 8, md: 12 }} position="relative">
          <SiteInfo 
            site={site} 
            media={augmentedMedia}
            onUpdate={handleSiteUpdate} 
            onFeatureClick={handleFeatureClick}
            filters={filters}
            onVisibilityChange={setHiddenLocations}
          />
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={{ base: 6, md: 10 }}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Media Gallery Section */}
          <Box>
            {mediaLoading ? (
              <Box
                bg="bg.surface"
                borderRadius="xl"
                p={{ base: 8, md: 12 }}
                textAlign="center"
                borderWidth="1px"
                borderColor="border.light"
                boxShadow="lg"
              >
                <VStack spacing={4}>
                  <Spinner 
                    size="xl" 
                    color="interactive.primary" 
                    thickness="3px"
                  />
                  <Text fontSize="lg" color="text.muted" fontFamily="heading">
                    Loading media gallery...
                  </Text>
                </VStack>
              </Box>
            ) : (
              <MediaGallery 
                site={site} 
                onMediaUploadComplete={handleMediaUploadComplete}
                onFeaturesUpdated={fetchSiteData}
                onMediaUpdated={loadMedia}
                filters={filters}
                filteredMedia={filteredMedia}
                media={augmentedMedia}
                onFiltersChange={setFilters}
              />
            )}
          </Box>

          {/* Discussion Section */}
          <Box
            bg="bg.surface"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="border.light"
            overflow="hidden"
            boxShadow="lg"
            _hover={{
              boxShadow: "xl",
              transform: "translateY(-2px)",
              transition: "all 0.3s ease"
            }}
            transition="all 0.3s ease"
          >
            {/* Section Header */}
            <Box
              bg="linear-gradient(90deg, interactive.primary 0%, accent.gold.600 100%)"
              px={{ base: 6, md: 8 }}
              py={4}
              borderBottom="1px solid"
              borderColor="border.light"
            >
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontFamily="heading"
                fontWeight="bold"
                color="white"
                textShadow="0 1px 2px rgba(0,0,0,0.1)"
              >
                Community Discussion
              </Text>
            </Box>

            {/* Content */}
            <Box p={{ base: 6, md: 8 }}>
              <CommentSection siteId={id} />
            </Box>
          </Box>
        </VStack>
      </Container>

      {/* Floating AI Assistant - Always available */}
      {site && <ChatBot site={enrichedSite || site} isAuthenticated={!!user} className="site-profile-chatbot" />}
    </Box>
  );
}
